<script setup>
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { useGridRef, useODataSource, useDefaultEditing, useDefaultGridOptions, useLookup, refreshGridAsync, getCreatedRowId } from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'
import { useChips } from '@/composables/chips'
import { planningApi } from '@/apis/planning.api'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import EquipmentConditionEdit from '@/components/shared/EquipmentConditionEdit.vue'

const i18n = getI18n()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const { gridRef } = useGridRef()
const { getConditionChips } = useChips()

const props = defineProps({
  equipment: { type: Object, default: null },
})

const addItem = ref(null)

const localizedConditions = planningStore.getLocalizedEquipmentConditions()

const getCreatedByName = (data) => {
  const staff = data.service_id ? planningStore.getStaffById(data.service_id) : null
  return staff ? planningStore.getServiceName(staff) : data.username
}

const gridDataSource = useODataSource(planningApi.getAbsoluteUrl() + planningApi.equipmentConditionsUrl, {
  customParamsFn: () => ({ equipment: props.equipment?.id }),
})

const columns = [
  {
    type: 'buttons',
    buttons: [
      {
        name: 'delete',
        visible: (e) => webSiteStore.canManageOwned(e.row?.data, webSiteStore.permissionFlagEnum.MASTER_DATA),
      },
    ],
    fixed: false,
    visibleIndex: -1,
    showInColumnChooser: false,
    width: '4rem',
  },
  dataGridColumn.useAvatarColumn({
    cellTemplate: (element, { data }) => {
      const staff = data.service_id ? planningStore.getStaffById(data.service_id) : null
      const src = staff?.avatar_file || staff?.avatar_url
      if (!src) return
      const img = document.createElement('img')
      img.src = src
      img.style.cssText = 'width:2rem;height:2rem;object-fit:cover;border-radius:50%;display:block;margin:auto'
      element.appendChild(img)
    },
  }),
  {
    caption: i18n.t('common.createdBy'),
    calculateCellValue: getCreatedByName,
    calculateSortValue: getCreatedByName,
    width: '12rem',
  },
  {
    dataField: 'created_at',
    caption: i18n.t('common.createdAt'),
    dataType: 'datetime',
    width: '10rem',
    sortOrder: 'desc',
  },
  {
    dataField: 'condition',
    caption: i18n.t('common.condition'),
    width: '8rem',
    lookup: useLookup({ dataSource: localizedConditions, valueExpr: 'id', displayExpr: 'name' }),
    cellTemplate: 'condition-cell-template',
  },
  {
    dataField: 'comment',
    caption: i18n.t('common.comment'),
    minWidth: '16rem',
  },
]

const editing = useDefaultEditing(null, null, { allowAdding: false, allowUpdating: false, allowDeleting: true })

const gridOptions = useDefaultGridOptions({
  id: 'equipment-conditions-grid',
  columns,
  dataSource: gridDataSource,
  editing,
  isDetailGrid: true,
  addButton: {
    onClick: () => { addItem.value = {} },
  },
})
gridOptions.height = '60vh'

const updateGrid = async(saved) => {
  const createdId = getCreatedRowId(addItem.value, saved)
  addItem.value = null
  await refreshGridAsync(gridRef, createdId)
}
</script>

<template>
  <div>
    <q-dialog
      :model-value="addItem !== null"
      class="dialog-md"
      @hide="updateGrid"
    >
      <equipment-condition-edit
        v-if="addItem !== null"
        :equipment="equipment"
        @save="updateGrid"
        @cancel="updateGrid"
      />
    </q-dialog>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #condition-cell-template="{ data: templateOptions }">
        <chip-template :values="getConditionChips(templateOptions.data.condition)" />
      </template>
    </dx-data-grid>
  </div>
</template>
